import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import api, { resolveImage } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Mail, Lock, ArrowRight } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { t, lang } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [coverImage, setCoverImage] = useState("");

  const next = location.state?.next || "/compte";

  useEffect(() => {
    api.get("/content").then((r) => setCoverImage(r.data?.content?.auth_login_image || "")).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(lang === "fr" ? `Bon retour, ${u.name}` : `Welcome back, ${u.name}`);
      navigate(u.role === "admin" ? "/admin" : next);
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === "fr" ? "Email ou mot de passe invalide" : "Invalid email or password"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white text-[var(--dsf-text)] min-h-screen">
      <Header />
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen pt-20" data-testid="login-page">
        <div className="relative hidden lg:block bg-gradient-to-br from-[var(--dd-green)] via-[var(--dd-green-deep)] to-[var(--dd-burgundy,#6E0D25)]">
          {coverImage && (
            <img src={resolveImage(coverImage)} alt="" className="absolute inset-0 w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-gradient-to-br from-[var(--dd-green)]/85 via-[var(--dd-green)]/60 to-transparent" />
          <div className="relative h-full flex flex-col justify-end p-12 text-white">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--dd-mustard)] mb-4">Maison Dakar</div>
            <h1 className="font-serif text-5xl xl:text-6xl leading-[1.05]">{t("auth.login.title")}</h1>
            <p className="text-white/85 mt-4 max-w-md">{t("auth.login.subtitle")}</p>
          </div>
        </div>

        <div className="flex items-center justify-center p-8 md:p-16 bg-transparent">
          <form onSubmit={submit} className="w-full max-w-md">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--dsf-orange)] mb-3">{t("nav.login")}</div>
            <h2 className="font-serif text-4xl text-[var(--dd-green)] mb-2">{t("auth.login.title")}</h2>
            <p className="text-[var(--dsf-text-secondary)] mb-8 text-sm">{t("auth.login.subtitle")}</p>

            <div className="mb-5">
              <label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-orange)] block mb-2 flex items-center gap-1.5"><Mail size={11} /> {t("common.email")}</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full border border-black/5 px-4 py-3 text-sm focus:border-[var(--dsf-green)] bg-white" data-testid="login-email" />
            </div>

            <div className="mb-8">
              <label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-orange)] block mb-2 flex items-center gap-1.5"><Lock size={11} /> {t("common.password")}</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full border border-black/5 px-4 py-3 text-sm focus:border-[var(--dsf-green)] bg-white" data-testid="login-password" />
            </div>

            <button type="submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="login-submit">
              {loading ? t("common.loading") : t("auth.login.cta")} <ArrowRight size={14} />
            </button>

            <div className="text-center text-sm text-[var(--dsf-text-secondary)] mt-6">
              {t("auth.login.no_account")}{" "}
              <Link to="/inscription" className="text-[var(--dsf-orange)] font-medium hover:underline" data-testid="link-register">{t("nav.register")}</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
